import { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isSameDay, addDays } from 'date-fns';

import { useUpdatingMemo } from 'utils/hooks';
import { isSameUser, isSameTimeRange } from 'utils/message';

import { LocaleContext } from 'context/LocaleContext';

import MessageRich from './message-rich';
import DateTime from 'components/DateTime';

const GroupHolder = ({ noDivider, children }) => {
    if (noDivider) {
        return children;
    }

    return <div className="messages-group">{ children }</div>
}

const Divider = ({ day }) => {
    const { t } = useTranslation('date');
    const localeCtx = useContext(LocaleContext);

    const title = useUpdatingMemo(() => {
        if (isSameDay(day, new Date())) {
            return t('periods.Today');
        }

        if (isSameDay(addDays(day, 1), new Date())) {
            return t('periods.Yesterday');
        }

        return null;
    }, [day, t]);

    return (
        <div className="discussion-item-divider" data-serialize-ignore="true">
            <span>
                { title || <DateTime format={localeCtx.dateTimeFormats.longDate}>{ day }</DateTime> }
            </span>
        </div>
    )

}

const WrapperElement = ({ noDivider, day, children }) => {
    if (noDivider) {
        return children;
    }

    return (
        <div className="discusison-day-group">
            <Divider day={day} />

            <div className="discusison-day-group-body">
                { children }
            </div>
        </div>
    )
}

const DiscussionDayGroup = ({ day, noDivider, messages }) => {
    const messageGroups = useMemo(() => {
        const groups = [];
        let previousMessage;

        messages.forEach(message => {
            if (message.loading) {
                return;
            }
            
            const merge = isSameUser(message, previousMessage) && isSameTimeRange(message, previousMessage) && (message.hidden || [])?.toString() === (previousMessage.hidden || [])?.toString();

            if (merge) {
                // Merge to last group
                groups[groups.length - 1].items.push(message);
            } else {
                // Create a new group
                groups.push({
                    id: message.author,
                    items: [message]
                });
            }

            previousMessage = message;
        });

        return groups;
    }, [messages]);

    return (
        <WrapperElement noDivider={noDivider} day={day}>
            {
                messageGroups.map((group, index) => (
                    <GroupHolder key={index} noDivider={noDivider}>
                        {
                            group.items.map((message, index) => (
                                <MessageRich
                                    key={message.id || index}
                                    id={message.id}
                                    type={message.type}
                                    text={message.text}
                                    url={message.url}
                                    docAttachment={message.doc_attachment}
                                    attachments={message.attachments}
                                    seenBy={message.seen_by}
                                    pending={!message.id}
                                    error={message.error}
                                    system={Boolean(message.subtype || !message.author)}
                                    systemMessageType={message.subtype}
                                    systemMessageData={message.system_message_data || message.system_comment_data}
                                    botName={message.bot_name}
                                    botAvatar={message.bot_avatar}
                                    edited={message.is_edited}
                                    removed={message.is_deleted}
                                    author={message.author}
                                    createdAt={message.created_at}
                                    replies={message.replies}
                                    repliers={message.repliers}
                                    reactions={message.reactions}
                                    hidden={message.hidden}
                                    hideLinkPreview={message.hide_link_preview}
                                    audioDuration={message.audio_duration}
                                    heardBy={message.heardBy}
                                    fragment={message.fragment}
                                />
                            ))
                        }
                    </GroupHolder>
                ))
            }
        </WrapperElement>
    );
}

DiscussionDayGroup.propTypes = {
    day: PropTypes.number,
    noDivider: PropTypes.bool,
    messages: PropTypes.array
};

export default DiscussionDayGroup;
